import { getFullName, getNormalizedTokens, peopleSearchSort, userByNameSort, UserNameFields } from '~/utils'
import {
  CacheUserFragment,
  CommunityType,
  GetMentionUserDocument,
  GetMentionUserQuery,
  Maybe,
  User,
} from '~/api/generated/graphql'
import { Suggestion } from '~/common/quill/QuillEditor'
import { ApolloCache } from '@apollo/client'

const NUM_SUGGESTIONS = 7

const MentionsService = {
  getMentions: async (
    searchRemoteUsers: (
      query: string,
      numSuggestions: number,
      veevanOnly?: boolean
    ) => Promise<Promise<Partial<CacheUserFragment>[] | undefined>>,
    search: string,
    renderList: (suggestions: Suggestion[], searchTerm: string) => void,
    cache: ApolloCache<object>,
    basicCommunity?: Maybe<{
      communityId: Maybe<string>
      type: Maybe<CommunityType>
      canEdit?: Maybe<boolean>
      photo?: Maybe<string>
      name?: string
    }>,
    memberIds?: string[],
    veevanOnly?: boolean
  ): Promise<void> => {
    const names = getNormalizedTokens(search)
    // Ordered list of permitted matches.  Will be supplemented with public matches if community is public
    const prefixCache = globalThis.prefixCache
    const memberIdSet: Set<string> = new Set(memberIds)

    const resultDict = new Map<string, number>()
    for (const tok of names) {
      const matches = prefixCache?.get(tok) ?? []
      for (const match of matches) {
        resultDict.set(match, (resultDict.get(match) || 0) + 1)
      }
    }
    const filteredResults = Array.from(resultDict.keys())
      .filter(k => resultDict.get(k) === names.length)
      .filter(k => k.startsWith('User'))
    // Matched userId to user object
    const result = new Map<string, GetMentionUserQuery['user']>()
    for (const k of filteredResults) {
      const id = k.split(':')[1]
      const user = cache.readQuery<GetMentionUserQuery>({
        query: GetMentionUserDocument,
        variables: { id },
      })?.user
      if (user) {
        result.set(id, user)
      }
    }

    // If not private, add all Veevan matches to the permitted match list
    // Can't add non-veevans for public communities because we may have restricted users.
    // We must rely on the search response to return the valid non-veevan users for private/public communities.
    if (basicCommunity?.type !== CommunityType.Private) {
      const resultList = Array.from(result.values()).filter(u => u?.isVeevan) as UserNameFields[]
      for (const u of resultList.sort(userByNameSort)) memberIdSet.add(u.userId)
    }

    // Drive the suggesting list off of the ordered permitted match list, so it retains priority order (and filters out
    // matches if needed)
    let matchedPeople: Suggestion[] = Array.from(memberIdSet.values())
      .filter(p => result.has(p))
      .map(p => result.get(p))
      .map((u: User) => {
        return {
          id: u.userId ?? null,
          user: u,
          value: getFullName(u),
          link: `/profiles/${u.userId}`,
          photo: u.photo ?? null,
        }
      })
    // Maybe add @Group or @All
    if (basicCommunity && basicCommunity?.canEdit && !veevanOnly) {
      const group = ['All', 'Group'].find(e => e.toLowerCase().startsWith(search.toLowerCase()))
      if (group) {
        matchedPeople.push({
          id: basicCommunity.communityId || '',
          community: basicCommunity,
          link: `/communities/${basicCommunity.communityId}`,
          value: group,
          photo: basicCommunity.photo ?? '',
        })
      }
    }

    matchedPeople = veevanOnly ? matchedPeople.filter(suggestion => suggestion.user?.isVeevan) : matchedPeople
    matchedPeople = matchedPeople
      .filter(
        suggestion => !suggestion.user?.hidden || suggestion?.community?.communityId === basicCommunity?.communityId
      )
      .sort((a, b) => {
        return peopleSearchSort(a, b, search, names, basicCommunity?.communityId || undefined, memberIdSet)
      })
      .slice(0, NUM_SUGGESTIONS)

    if (matchedPeople.length < NUM_SUGGESTIONS) {
      const users = await searchRemoteUsers(search, NUM_SUGGESTIONS - matchedPeople.length, veevanOnly)
      const remoteMatches: Suggestion[] =
        users
          ?.filter(u => !memberIdSet.has(u.userId ?? ''))
          .map((u: User) => ({
            id: u.userId ?? null,
            user: u,
            value: getFullName(u),
            link: `/profiles/${u.userId}`,
            photo: u.photo ?? null,
          })) ?? []
      matchedPeople.push(...remoteMatches)
    }
    // Selecting a user in the mention dropdown before the server results come back will cause the menu to
    // get re-rendered with the server results even though you already selected a mention.
    // Only render the list once here after the remote user search.
    renderList(matchedPeople, search)
  },
}

export default MentionsService
